import React from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "@/components/ui/pagination";

export const ITEMS_PER_PAGE = 25;

export function usePagination(items, perPage = ITEMS_PER_PAGE) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));

  // Reset to page 1 when items change (e.g. filter applied)
  React.useEffect(() => {
    setCurrentPage(1);
  }, [items.length]);

  const paginatedItems = items.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems,
    totalItems: items.length,
    startIndex: (currentPage - 1) * perPage + 1,
    endIndex: Math.min(currentPage * perPage, items.length),
  };
}

export const TablePagination = ({ currentPage, totalPages, totalItems, startIndex, endIndex, onPageChange }) => {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("ellipsis-start");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("ellipsis-end");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100" data-testid="table-pagination">
      <span className="text-xs text-slate-500">
        Showing {startIndex}–{endIndex} of {totalItems}
      </span>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              className={currentPage === 1 ? "pointer-events-none opacity-40" : "cursor-pointer"}
              data-testid="pagination-prev"
            />
          </PaginationItem>
          {getVisiblePages().map((page, i) =>
            typeof page === "string" ? (
              <PaginationItem key={page}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={page}>
                <PaginationLink
                  isActive={page === currentPage}
                  onClick={() => onPageChange(page)}
                  className="cursor-pointer"
                  data-testid={`pagination-page-${page}`}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            )
          )}
          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              className={currentPage === totalPages ? "pointer-events-none opacity-40" : "cursor-pointer"}
              data-testid="pagination-next"
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
